import { TextField } from '@atgfr8/atg-ui-tailwind';
import {
  hazardousMaterials,
  packingGroups,
  shipmentPackagingTypes,
} from '@common/commonLists';
import { HazardousMaterialSchema } from '@common/stops';
import { Button, SelectInput, PhoneInput } from '@components';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { LTLHazmatDetails } from '@store/ltl';
import { setOrderStore } from '@store/orders';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { createEffect, createSignal } from 'solid-js';

import cls from './classes';
interface HazmatMaterialProps {
  loadItemHazmatDetail: LTLHazmatDetails;
  onSave: (data: LTLHazmatDetails) => void;
  onClose: () => void;
  fromSidebar: boolean;
}
export const HazardousMaterial = (props: HazmatMaterialProps) => {
  const parsePrefix = (value: string | undefined | null): 'UN' | 'NA' => {
    const trimmed = (value ?? '').trim().toUpperCase();
    return trimmed.startsWith('NA') ? 'NA' : 'UN';
  };

  const parseDigits = (value: string | undefined | null): string => {
    const trimmed = (value ?? '').trim().toUpperCase();
    if (trimmed.startsWith('UN') || trimmed.startsWith('NA')) {
      return trimmed.slice(2).replaceAll(/\D/g, '').slice(0, 4);
    }
    return trimmed.replaceAll(/\D/g, '').slice(0, 4);
  };

  const combineUnNumber = (prefix: string, digits: string): string => {
    if (!digits) return '';
    return `${prefix}${digits}`;
  };

  const initialUN = props.loadItemHazmatDetail.unNumber ?? '';
  const [hazPrefix, setHazPrefix] = createSignal<'UN' | 'NA'>(
    parsePrefix(initialUN),
  );
  const [hazDigits, setHazDigits] = createSignal(parseDigits(initialUN));

  const { form, errors, data, setFields, isDirty, setIsDirty } = createForm({
    initialValues: {
      loadItemId: props.loadItemHazmatDetail.loadItemId,
      unNumber: combineUnNumber(parsePrefix(initialUN), parseDigits(initialUN)),
      packingGroup: props.loadItemHazmatDetail.packingGroup,
      hazardClass: props.loadItemHazmatDetail.hazardClass,
      piece: props.loadItemHazmatDetail.piece,
      contactNumber: props.loadItemHazmatDetail.contactNumber,
      contactName: props.loadItemHazmatDetail.contactName ?? '',
    },
    extend: validator({ schema: HazardousMaterialSchema }),
    onSubmit: () => {
      if (props.fromSidebar) {
        props.onSave(data());
      } else {
        setOrderStore('isStopsEdited', isDirty());
        props.onSave({
          ...data(),
        });
      }
    },
  });

  const setStopsFields = (
    field:
      | 'unNumber'
      | 'packingGroup'
      | 'hazardClass'
      | 'piece'
      | 'contactNumber'
      | 'contactName',
    value: string,
  ) => {
    setIsDirty(true);
    setFields(field, value);
  };

  const prefixOptions = [
    { label: 'UN', value: 'UN' },
    { label: 'NA', value: 'NA' },
  ];

  const updateUnNumber = (prefix: string, digits: string) => {
    setStopsFields('unNumber', combineUnNumber(prefix, digits));
  };

  createEffect(() => {
    const hazmatDetail = props.loadItemHazmatDetail;
    const unNumber = hazmatDetail.unNumber ?? '';
    const loadItemId = hazmatDetail.loadItemId ?? 0;

    setHazPrefix(parsePrefix(unNumber));
    setHazDigits(parseDigits(unNumber));
    setFields('loadItemId', loadItemId);
    setFields(
      'unNumber',
      combineUnNumber(parsePrefix(unNumber), parseDigits(unNumber)),
    );
    setFields('packingGroup', hazmatDetail.packingGroup ?? '');
    setFields('hazardClass', hazmatDetail.hazardClass ?? '');
    setFields('piece', hazmatDetail.piece ?? '');
    setFields('contactNumber', hazmatDetail.contactNumber ?? '');
    setFields('contactName', hazmatDetail.contactName ?? '');
    setIsDirty(false);
  });

  return (
    <form ref={form} class={cls.popoverClass}>
      <span class={`${cls.modalHeadingLeftAlign}`}>
        Edit Hazardous Material
      </span>
      <div class="flex flex-col gap-3 p-4">
        <div class="flex gap-x-2">
          <div class="w-[30%]">
            <SelectInput
              label="Prefix"
              menuItems={prefixOptions.map((p) => ({
                label: p.label,
                value: String(p.value),
              }))}
              value={hazPrefix()}
              onChange={(newPrefix) => {
                const p = newPrefix as 'UN' | 'NA';
                setHazPrefix(p);
                updateUnNumber(p, hazDigits());
              }}
              dataTestId={formatDataTestId('Prefix', 'selectInput')}
            />
          </div>
          <div class="w-[70%]">
            <TextField
              dataTestId="un-number-textInput"
              label="UN/NA Number*"
              placeholder="Enter Number*"
              type="text"
              maxLength={4}
              style={{ width: '100%' }}
              value={hazDigits()}
              onChange={(val: string) => {
                const digitsOnly = val.replace(/[^0-9]/g, '').slice(0, 4);
                setHazDigits(digitsOnly);
                updateUnNumber(hazPrefix(), digitsOnly);
              }}
              error={errors().unNumber}
            />
          </div>
        </div>
        <SelectInput
          name="packingGroup"
          label="Packing Group"
          menuItems={packingGroups.map((item) => ({
            label: item.label,
            value: String(item.value),
          }))}
          value={data().packingGroup}
          onChange={(value: string) => setStopsFields('packingGroup', value)}
          placeholder="Select Packing Group"
          dataTestId={formatDataTestId('Packing Group', 'selectInput')}
        />
        <SelectInput
          name="hazardClass"
          label="Hazard Class"
          menuItems={hazardousMaterials.map((item) => ({
            label: item.label,
            value: String(item.value),
          }))}
          value={data().hazardClass}
          onChange={(value) => setStopsFields('hazardClass', value)}
          placeholder="Select Hazard Class"
          dataTestId={formatDataTestId('Hazard Class', 'selectInput')}
        />
        <SelectInput
          name="piece"
          label="Piece Description"
          menuItems={shipmentPackagingTypes.map((item) => ({
            label: item.label,
            value: String(item.value),
          }))}
          value={data().piece}
          onChange={(value) => setStopsFields('piece', value)}
          placeholder="Select Piece Description"
          dataTestId={formatDataTestId('Piece Description', 'selectInput')}
        />
        <PhoneInput
          label="Emergency Contact Number"
          required
          placeholder="Emergency Contact Number*"
          error={errors().contactNumber}
          value={data().contactNumber}
          onChange={(phone) => setStopsFields('contactNumber', phone)}
          dataTestId="emergency-contact-number-phoneInput"
        />
        <TextField
          dataTestId="emergency-contact-name-textInput"
          name="contactName"
          label="Emergency Contact Name*"
          placeholder=""
          style={{ width: '100%' }}
          value={data().contactName}
          onChange={(name: string) => setStopsFields('contactName', name)}
          error={errors().contactName}
        />
        <div class="flex justify-end">
          <Button
            label="Save"
            type="submit"
            sx={{ margin: '0 4px 0 0 ' }}
            dataTestId={formatDataTestId('Save', 'button')}
          />
          <Button
            label="Cancel"
            sx={{ 'margin-bottom': '0 0 0 4px' }}
            variant="text"
            onClick={props.onClose}
            dataTestId={formatDataTestId('Cancel', 'button')}
          />
        </div>
      </div>
    </form>
  );
};
